import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import _ from 'lodash';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { CheckCircleOutlined, ClockCircleOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { Tooltip, Button, Row, Card, Space, Modal, message } from 'antd';
import { checkPermission } from '@/utils/permission';
import { cleanObject, formatTime, isValidDate } from '@/utils/helpers';
import BaseSelect from '@/components/Elements/BaseSelect';
import moment from 'moment';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import { approveTreatmentSession, approveTreatmentSessionBulk, getListTreatmentSession, unapproveTreatmentSession, unapproveTreatmentSessionBulk } from '@/redux/actions/treatmentSessionActions';
import DynamicTable from '@/components/DynamicTable';
import StatisticNumber from '@/components/StatisticNumber';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import HtmlParser from 'react-html-parser';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/treatment-sessions', breadcrumbName: 'Phiếu điều trị' },
];

const getTreatmentSessionServices = (record) => {
    const customerServices = [
        record?.customer_services,
        record?.customerServices,
        record?.services,
    ].find(items => Array.isArray(items) && items.length > 0) || [];

    return customerServices.map((item, index) => ({
        id: item?.id || item?.customer_service_id || item?.customerServiceId || index,
        name: item?.customer_service?.service?.name
            || item?.customerService?.service?.name
            || item?.service?.name
            || item?.service_name
            || item?.name
            || null,
    })).filter(item => item.name);
};

export default function TreatmentSessionList() {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const items = useSelector(state => state.treatmentSessionReducer.treatmentSessionList?.list);
    const pagination = useSelector(state => state.treatmentSessionReducer.treatmentSessionList?.pagination);
    const statistics = useSelector(state => state.treatmentSessionReducer.treatmentSessionList?.statistics);
    const doctors = useSelector(state => state.config.doctors);
    const assistants = useSelector(state => state.config.assistants);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const allowApproved = checkPermission('treatment_sessions', 'approve');
    const allowUnapproved = checkPermission('treatment_sessions', 'unapprove');

    const query = React.useMemo(
        () => qs.parse(location.search.slice(1)),
        [location.search]
    );

    const loadData = useCallback((q) => {
        setIsLoading(true);
        dispatch(getListTreatmentSession(q || query))
            .finally(() => setIsLoading(false));
    }, [dispatch, query]);

    useEffect(() => {
        loadData(query);
    }, [location.search]);

    const handleChangeTable = (pagination, sorter) => {
        let q = { ...query };

        if (!_.isEmpty(pagination)) {
            q.page = pagination.current;
            q.per_page = pagination.pageSize;
        }

        if (!_.isEmpty(sorter)) {
            if (sorter.order && sorter.field) {
                q.sort_field = sorter.field;
                q.sort_type = sorter.order === 'ascend' ? 'ASC' : 'DESC';
            } else {
                delete q.sort_field;
                delete q.sort_type;
            }
        }

        history.push({
            pathname: location.pathname,
            search: qs.stringify(q),
        });
    };

    const todayRange = () => {
        const today = moment();
        return {
            date_treatment_from: today.format('YYYY-MM-DD'),
            date_treatment_to: today.format('YYYY-MM-DD'),
        };
    };

    useEffect(() => {
        if (!query.date_treatment_from && !query.date_treatment_to) {
            history.replace({
                pathname: location.pathname,
                search: qs.stringify({
                    ...query,
                    ...todayRange(),
                }),
            });
        }
    }, [location.search]);

    const formatDate = (d) => (moment.isMoment(d) && d.isValid()) ? d.format('YYYY-MM-DD') : undefined;

    const handleFilterChange = (name, value) => {
        let q = { ...query, [name]: value };

        const rangeFilters = {
            date_treatment: ['date_treatment_from', 'date_treatment_to'],
        };

        if (rangeFilters[name]) {
            const [fromKey, toKey] = rangeFilters[name];
            const [start, end] = value || [];

            q[fromKey] = formatDate(start);
            q[toKey] = formatDate(end);

            // Nếu không có ngày hợp lệ, xóa cả hai
            if (!q[fromKey] && !q[toKey]) {
                delete q[fromKey];
                delete q[toKey];
            }

            delete q[name];
        }
        console.log(name, value);
        q = cleanObject(q);
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    const handleApprove = async (record) => {
        if (!record?.id) {
            message.error("Chưa có phiếu điều trị để xác nhận");
            return;
        }

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xác nhận phiếu điều trị?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(approveTreatmentSession(record.id));
                    await loadData(query); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleUnapprove = async (record) => {
        if (!record?.id) return;

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn hủy xác nhận phiếu điều trị?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(unapproveTreatmentSession(record.id));
                    await loadData(query); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleApproveBulk = () => {
        if (!selectedRowKeys.length) {
            message.warning('Vui lòng chọn ít nhất 1 phiếu điều trị');
            return;
        }

        Modal.confirm({
            title: 'Xác nhận hàng loạt',
            content: `Bạn có chắc muốn xác nhận ${selectedRowKeys.length} phiếu điều trị đã chọn?`,
            okText: 'Đồng ý',
            cancelText: 'Hủy',
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(approveTreatmentSessionBulk(selectedRowKeys));
                    setSelectedRowKeys([]);
                    await loadData(query);
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleUnapproveBulk = () => {
        if (!selectedRowKeys.length) {
            message.warning('Vui lòng chọn ít nhất 1 phiếu điều trị');
            return;
        }

        Modal.confirm({
            title: 'Hủy xác nhận hàng loạt',
            content: `Bạn có chắc muốn hủy xác nhận ${selectedRowKeys.length} phiếu điều trị đã chọn?`,
            okText: 'Đồng ý',
            cancelText: 'Hủy',
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(unapproveTreatmentSessionBulk(selectedRowKeys));
                    setSelectedRowKeys([]);
                    await loadData(query);
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };


    const actionColumn = {
        title: 'Actions',
        key: 'actions',
        width: 120,
        align: 'center',
        render: (_, record) => {
            const isApproved = record.is_approved || false;
            return (
                <>
                    {isApproved ? (
                        <>
                            <Space align="center" size="middle">
                                {allowUnapproved && (
                                    <Tooltip title="Hủy xác nhận">
                                        <Button
                                            htmlType="button"
                                            icon={<CloseOutlined />}
                                            onClick={() => handleUnapprove(record)}
                                            danger
                                        >
                                            Hủy xác nhận
                                        </Button>
                                    </Tooltip>
                                )}
                            </Space>
                        </>
                    ) : (
                        allowApproved && (
                            <Tooltip title="Xác nhận của bác sĩ">
                                <Button
                                    type="primary"
                                    style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handleApprove(record)}
                                    disabled={false}
                                >
                                    Xác nhận
                                </Button>
                            </Tooltip>
                        )
                    )}
                </>
            )
        },
    };

    const baseColumns = [
        {
            title: 'Thủ thuật thực hiện',
            dataIndex: 'procedures',
            key: 'procedures',
            width: 360,
            render: (_, record) => {
                const doctors = record.doctors || null;
                const assistants = record.assistants || [];

                return (
                    <div style={{ lineHeight: 1.4 }}>

                        {/* ===== Danh sách thủ thuật ===== */}
                        <div style={{ marginBottom: 8 }}>
                            {record.procedures?.map((p, i) => (
                                <div
                                    key={p.id || i}
                                    style={{
                                        marginBottom: 6,
                                        paddingLeft: 8,
                                        borderLeft: '3px solid #e6f4ff',
                                        marginLeft: -8,
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    <div style={{ fontWeight: 600 }}>
                                        {p.name}
                                    </div>

                                    {p.description && (
                                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                                            {p.description}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* ===== Nhân sự (metadata) ===== */}
                        <div style={{ fontSize: 12, color: '#595959', marginBottom: 4 }}>
                            {doctors && doctors.length > 0 && (
                                <div>
                                    {doctors.map((staff, i) => (
                                        <div key={staff.doctor?.id || i}>
                                            <span style={{ color: '#8c8c8c' }}>
                                                {staff.doctor?.user_role?.name || 'Bác sĩ'}:
                                            </span>{' '}
                                            <span style={{ fontWeight: 500 }}>
                                                {staff.doctor?.full_name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {assistants && assistants.length > 0 && (
                                <div>
                                    {assistants.map((staff, i) => (
                                        <div key={staff.assistant?.id || i}>
                                            <span style={{ color: '#8c8c8c' }}>
                                                {staff.assistant?.user_role?.name || 'Phụ tá'}:
                                            </span>{' '}
                                            <span style={{ fontWeight: 500 }}>
                                                {staff.assistant?.full_name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ===== Ngày & giờ ===== */}
                        <div
                            style={{
                                fontSize: 12,
                                color: '#8c8c8c',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                borderTop: '1px dashed #f0f0f0',
                                paddingTop: 6,
                            }}
                        >
                            <ClockCircleOutlined style={{ fontSize: 13 }} />
                            <span>
                                <strong>Ngày điều trị:</strong> {isValidDate(record.ngay, false)} {formatTime(record.gio)}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Khách hàng',
            key: 'customer',
            width: 300,
            render: (_, record) => {
                const customer = {
                    id: record?.customer?.id,
                    code: record?.customer?.code,
                    full_name: record?.customer?.full_name,
                    branch_name: record?.customer?.branch?.name,
                    medical_book_number: record?.customer?.medical_book_number
                };

                const services = getTreatmentSessionServices(record);

                return (
                    <div style={{ lineHeight: 1.4 }}>

                        {/* ===== Khách hàng ===== */}
                        <CustomerInfoColumn
                            record={customer}
                            nameField="full_name"
                            idField="id"
                            codeField="code"
                            branchField="branch_name"
                            medicalBookField="medical_book_number"
                        />

                        {/* ===== Dịch vụ ===== */}
                        {services.length > 0 && (
                            <div
                                style={{
                                    marginTop: 6,
                                    paddingTop: 6,
                                    borderTop: '1px dashed #dfdddd',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: '#8c8c8c',
                                        marginBottom: 4,
                                    }}
                                >
                                    Dịch vụ:
                                </div>

                                <div style={{ fontSize: 12, color: '#595959' }}>
                                    {services.map((item, idx) => (
                                        <div
                                            key={item.id || idx}
                                            style={{
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                marginBottom: 2,
                                            }}
                                        >
                                            • {item.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Ghi chú nội bộ',
            dataIndex: 'ghi_chu_noi_bo',
            key: 'ghi_chu_noi_bo',
            width: 200,
            render: (_, record) => {
                return (
                    <div style={{ wordBreak: 'break-word' }}>{HtmlParser(record?.ghi_chu_noi_bo)}</div>
                );
            },
        }
    ];

    const columns = React.useMemo(() => {
        const cols = [];

        // Chỉ push Actions column nếu CÓ ÍT NHẤT 1 quyền
        if (allowApproved || allowUnapproved) {
            cols.push(actionColumn);
        }

        cols.push(...baseColumns);

        return cols;
    }, [allowApproved, allowUnapproved]);

    return (
        <div>
            <PageTitle routes={routes} title="Phiếu điều trị" />
            <TableActionBar
                isShowAddButton={false}
                isShowDeleteButton={false}
                disabled={!selectedRowKeys.length}
                showSearch
                showFilter={true}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                placeholderSearch="Mã, tên, 3 số cuối sđt của khách hàng"
                filterColSpan={6}
                activeFilter={
                    Object.keys(query).length > 0 && !query.keyword && !query.date_treatment_from ? true : false
                }
                filters={[
                    <BaseSelect
                        options={[
                            { value: 1, label: 'Đã xác nhận' },
                            { value: 0, label: 'Chưa xác nhận' }
                        ]}
                        showSearch
                        placeholder="Trạng thái"
                        className="w-100"
                        onChange={(value) => handleFilterChange("status", value)}
                        defaultValue={query.status ? parseInt(query.status) : null}
                    />,
                    <BaseSelect
                        options={doctors || []}
                        optionValue="user_id"
                        optionLabel="display_name"
                        showSearch
                        placeholder="Bác sĩ phụ trách"
                        className="w-100"
                        onChange={(value) => handleFilterChange('doctor_id', value)}
                        defaultValue={query.doctor_id ? parseInt(query.doctor_id) : null}
                    />,
                    <BaseSelect
                        options={assistants || []}
                        optionValue="user_id"
                        optionLabel="display_name"
                        showSearch
                        placeholder="Phụ tá phụ trách"
                        className="w-100"
                        onChange={(value) => handleFilterChange('assistant_id', value)}
                        defaultValue={query.assistant_id ? parseInt(query.assistant_id) : null}
                    />
                ]}
            >
                {allowApproved && (
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        disabled={!selectedRowKeys.length}
                        onClick={handleApproveBulk}
                        className="table-button"
                    >
                        Xác nhận phiếu đã chọn
                    </Button>
                )}

                {allowUnapproved && (
                    <Button
                        danger
                        icon={<CloseOutlined />}
                        disabled={!selectedRowKeys.length}
                        onClick={handleUnapproveBulk}
                        className="table-button"
                    >
                        Hủy xác nhận đã chọn
                    </Button>
                )}
                <BaseRangeFilter
                    name="date_treatment"
                    query={query}
                    width={300}
                    className="table-button"
                    onChangeFilter={handleFilterChange}
                    placeholder={['Ngày điều trị', 'Đến ngày']}
                />
                <Button className='table-button' icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData(query)}>Refresh</Button>
                {/* {selectedRowKeys.length > 0 && <span>Đang chọn {selectedRowKeys.length} bản ghi</span>} */}
            </TableActionBar>

            <Row gutter={16} className="mb-2 mt-2" justify="space-between" style={{ margin: 0 }}>
                <Card className="treatment-sessions-statistic">
                    <StatisticNumber
                        title="Tổng phiếu"
                        value={statistics?.total || 0}
                        precision={0}
                    />
                </Card>
                <Card className="treatment-sessions-statistic">
                    <StatisticNumber
                        title="Phiếu đã xác nhận"
                        value={statistics?.approved_total || 0}
                        precision={0}
                    />
                </Card>
                <Card className="treatment-sessions-statistic">
                    <StatisticNumber
                        title="Phiếu chưa xác nhận"
                        value={statistics?.not_approved_total || 0}
                        precision={0}
                    />
                </Card>
            </Row>

            <div className='has-custom-column-table-btn'>
                <DynamicTable
                    rowKey="id"
                    rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                    dataSource={items || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={pagination}
                    moduleKey="treatment_sessions"
                    onChangeTable={(pagination, sorter) => handleChangeTable(pagination, sorter)}
                />
            </div>
        </div>
    );
}
